// API Service - Centralized API calls
// Handles all HTTP requests to the backend

import axios from 'axios';
import { cachedApiCall, cache } from '../lib/cache';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://altiti.com/analysis1/api';

// Grace period flag: prevents the 401 interceptor from firing right after a fresh login
let _suppressSessionExpiry = false;
export function suppressSessionExpiryBriefly() {
  _suppressSessionExpiry = true;
  setTimeout(() => { _suppressSessionExpiry = false; }, 3000);
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      const networkError = new Error('فشل الاتصال بالخادم. تحقق من اتصال الإنترنت.');
      return Promise.reject(networkError);
    }

    // Handle specific status codes
    const status = error.response.status;
    const errorMessage = error.response.data?.message || error.response.data?.error;

    switch (status) {
      case 401: {
        // Token expired or invalid — clear storage and notify the app
        // Skip if we're in a post-login grace period to avoid false positives
        if (!_suppressSessionExpiry) {
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        return Promise.reject(new Error('انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.'));
      }
      
      case 403:
        return Promise.reject(new Error(errorMessage || 'ليس لديك صلاحية للوصول إلى هذا المورد.'));
      
      case 404:
        return Promise.reject(new Error(errorMessage || 'المورد المطلوب غير موجود.'));
      
      case 429:
        return Promise.reject(new Error('تم تجاوز عدد المحاولات المسموح بها. يرجى المحاولة لاحقاً.'));
      
      case 500:
        return Promise.reject(new Error(errorMessage || 'خطأ في الخادم. يرجى المحاولة لاحقاً.'));
      
      default:
        return Promise.reject(new Error(errorMessage || `حدث خطأ غير متوقع (${status})`));
    }
  }
);

// Auth API — uses plain axios (no interceptor) so login/register 401s are
// treated as wrong credentials, NOT as session-expiry events.
const authClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export const authAPI = {
  login: async (email: string, password: string) => {
    try {
      const response = await authClient.post('/auth.php?action=login', { email, password });
      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(msg || 'بريد إلكتروني أو كلمة مرور غير صحيحة');
    }
  },

  register: async (email: string, password: string, name: string) => {
    try {
      const response = await authClient.post('/auth.php?action=register', { email, password, name });
      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(msg || 'فشل إنشاء الحساب');
    }
  },

  verifyToken: async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const response = await authClient.get('/auth.php?action=user', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Quota API
export const quotaAPI = {
  check: async () => {
    // Cache quota check for 1 minute to reduce server load
    return cachedApiCall(
      'quota_check',
      async () => {
        const response = await apiClient.get('/check-quota.php');
        return response.data;
      },
      60000 // 1 minute
    );
  },

  increment: async () => {
    const response = await apiClient.post('/increment-usage.php');
    // Clear quota cache after increment
    cache.delete('quota_check');
    return response.data;
  },
};

// Survey API
export const surveyAPI = {
  save: async (data: {
    file_data: unknown[];
    workshop_column: string;
    axes?: unknown;
    scale_mapping?: unknown;
    analysis_results?: unknown;
  }) => {
    const response = await apiClient.post('/surveys.php?action=create', data);
    return response.data;
  },

  update: async (id: number, data: {
    file_data?: unknown[];
    workshop_column?: string;
    axes?: unknown;
    scale_mapping?: unknown;
    analysis_results?: unknown;
  }) => {
    const response = await apiClient.post('/surveys.php?action=update', { id, ...data });
    return response.data;
  },

  list: async () => {
    const response = await apiClient.get('/surveys.php?action=list');
    return response.data;
  },

  get: async (id: number) => {
    const response = await apiClient.get(`/surveys.php?action=get&id=${id}`);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await apiClient.post('/surveys.php?action=delete', { id });
    return response.data;
  },
};

// User Management API (Admin only)
export const userAPI = {
  list: async () => {
    const response = await apiClient.get('/users.php?action=list');
    return response.data;
  },

  create: async (data: {
    email: string;
    password: string;
    name: string;
    role?: string;
    analysis_quota?: number;
  }) => {
    const response = await apiClient.post('/users.php?action=create', data);
    return response.data;
  },

  update: async (id: number, data: {
    email?: string;
    full_name?: string;
    password?: string;
    role?: string;
    analysis_quota?: number;
  }) => {
    const response = await apiClient.post('/users.php?action=update', { id, ...data });
    return response.data;
  },

  delete: async (id: number) => {
    const response = await apiClient.post('/users.php?action=delete', { id });
    return response.data;
  },

  resetUsage: async (id: number) => {
    const response = await apiClient.post('/users.php?action=update', { 
      id, 
      reset_usage: true 
    });
    return response.data;
  },
};

// Export API
export const exportAPI = {
  exportData: async (data: unknown[], format: 'csv' | 'json' | 'excel') => {
    const response = await apiClient.post('/export.php', { data, format }, {
      responseType: format === 'csv' || format === 'excel' ? 'blob' : 'json',
    });
    return response.data;
  },
};

export default apiClient;
